import { Box, TextareaAutosize } from '@mui/material'
import { ApproveInGroup, CheckmarkIcon, PencilIcon } from 'assets/icons'
import { useConnection } from 'components/providers/ConnectionProvider'
import { useProgress } from 'components/providers/ProgressProvider'
import i18n from 'i18n'
import { ChangeEvent } from 'react'
import { useState } from 'react'
import { updateGroupBI } from 'services/api/group'
import { isOwnerForGroup } from 'services/db/utils/GroupUtils'
import Logger from 'services/Logger'
import { AccountDoc, GroupDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { GENERIC_ERROR } from 'utils/strings'
import * as Styled from '../styled'
import {
  featureToMinimumTierNeeded,
  isUserAdminInGroup
} from 'utils/api/groups'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { Tier, highestActiveTier, paddlePlanToTier } from 'utils/api/accounts'
import { usePayment } from 'components/providers/PaymentProvider'
import { PlanName } from 'components/dialogs/payment/PlansInfoDialog'
import { tierToPlan } from 'components/tier/TierHover'
import { tierFeaturesToAnalyticsFeature } from 'components/tier/OutOfTierDialog'
import { parseErrorToMessage } from '../eventInfo/EventPopup'
import { theme } from 'theme'
import { TierFeatures } from 'utils/api/group_state'
import { t } from 'i18next'
import { isTrulyEmpty } from 'utils/object'
import { ProBadgeContainer } from 'components/badges/ProBadge'
import IconButton from 'components/buttons/IconButton'
import PopupSection from 'components/popup/PopupSection'

interface BusinessInfoProps {
  group: Model<GroupDoc>
  account: Model<AccountDoc>
  setLoading: (b: boolean) => void
  editable?: boolean
}

export default function BusinessInfoSection(props: BusinessInfoProps) {
  const { showPaymentDialog } = usePayment()
  const activeTier = highestActiveTier(props.account)
  const connection = useConnection()
  const progress = useProgress()
  const [text, setText] = useState<string>(props.group.BusinessInfo?.Text ?? '')
  const [editBI, onEditBI] = useState(false)

  const handleClose = (
    status: 'PURCHASED' | 'PURCHASE_ABORTED' | 'DIALOG_CLOSED',
    plan: PlanName
  ) => {
    Logger.debug('plan status; plan; =>', status, plan)

    const shouldHandleAction = paddlePlanToTier(plan) >= Tier.BUSINESS

    console.log(shouldHandleAction)

    if (shouldHandleAction) onEditBI(true)
  }

  const openPayments = () => {
    showPaymentDialog(tierToPlan(Tier.BUSINESS), handleClose, [
      tierFeaturesToAnalyticsFeature(TierFeatures.BUSINESSINFO)
    ])
  }

  const toggleEditBI = () => {
    if (
      activeTier < featureToMinimumTierNeeded(TierFeatures.BUSINESSINFO) &&
      isOwnerForGroup(props.group)
    ) {
      openPayments()
      return
    }

    if (connection.isConnectionAvailable()) onEditBI(!editBI)
  }
  const onSubmitChangeBI = async () => {
    toggleEditBI()

    if (text != props.group.BusinessInfo?.Text && !isTrulyEmpty(text)) {
      props.setLoading(true)
      try {
        await updateGroupBI(text, props.group)
      } catch (e) {
        Logger.red(String(e))
        progress.setError(parseErrorToMessage(e))
      }

      props.setLoading(false)
    }
  }
  const onBITextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value)
  }

  const isGroupAdmin = isUserAdminInGroup(
    props.group,
    LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  )
  const canEditBusinessInfo = isOwnerForGroup(props.group) || isGroupAdmin

  function title() {
    return (
      <Box
        fontSize={'14px'}
        fontWeight={400}
        color={
          props.group.BusinessInfo?.Text
            ? theme.palette.text.primary
            : '#818181'
        }
        onClick={
          activeTier >= featureToMinimumTierNeeded(TierFeatures.BUSINESSINFO)
            ? toggleEditBI
            : openPayments
        }
      >
        {props.group.BusinessInfo?.Text ?? t('addCalendarDescription')}
      </Box>
    )
  }

  if (
    (!props.editable || !canEditBusinessInfo) &&
    isTrulyEmpty(props.group.BusinessInfo?.Text)
  ) {
    return null
  }

  return (
    <PopupSection
      sectionId="aboutSection"
      title={`${t('aboutCalendar', { calendarName: props.group.Name })}`}
      simplified
      popupSectionGap={'8px'}
    >
      <Box
        display={'flex'}
        flexDirection={'row'}
        style={{ width: '100%' }}
        gap={2}
        alignItems={'flex-start'}
      >
        <Box display={'flex'} flex={1}>
          <Box
            sx={{
              pointerEvents: canEditBusinessInfo ? 'auto' : 'none',
              borderRadius: '8px',
              padding: theme.spacing(1, 1),
              marginLeft: i18n.dir() === 'ltr' ? -1 : undefined,
              marginRight: i18n.dir() === 'rtl' ? -1 : undefined,
              width: '100%',
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
                cursor: 'pointer'
              }
            }}
          >
            {editBI ? (
              <TextareaAutosize
                autoFocus
                style={{ padding: 8, width: '100%', fontSize: '14px' }}
                placeholder={t('addCalendarDescription')}
                value={text}
                minRows={3}
                onChange={onBITextChange}
              />
            ) : activeTier >=
              featureToMinimumTierNeeded(TierFeatures.BUSINESSINFO) ? (
              title()
            ) : (
              <ProBadgeContainer>{title()}</ProBadgeContainer>
            )}
          </Box>
        </Box>
        {editBI && (
          <IconButton onClick={onSubmitChangeBI} size="xs">
            {editBI ? <ApproveInGroup color="green" /> : <></>}
          </IconButton>
        )}
      </Box>
    </PopupSection>
  )
}
